import { type Voice } from '@/domain/book';
import {
  type ITTSEngine,
  type ITTSService,
  type SpeakChunk,
  type SpeakOptions,
  type TTSHandlers,
  type TTSProvider,
  type TTSProviderId,
  type TTSStatus,
  TTS_PROVIDERS,
} from '@/services/TTSService';
import { type ILoadableTTSEngine } from './KokoroTTSService';

/**
 * The one TTS service the player sees. Holds both engines and forwards the {@link ITTSEngine} half
 * (enqueue/stop/voices/callbacks) to whichever provider is active. Switching to Kokoro loads its
 * model on first use, reporting progress through {@link onStatus} so the UI can gate Play behind a
 * download bar. A failed load leaves the previous provider active and untouched.
 */
export class TTSRouter implements ITTSService {
  private active: ITTSEngine;
  private activeId: TTSProviderId = 'system';
  private handlers: TTSHandlers = {};
  private statusCb: ((status: TTSStatus) => void) | undefined;

  constructor(
    private readonly system: ITTSEngine,
    private readonly kokoro: ILoadableTTSEngine,
  ) {
    this.active = system;
  }

  initialize(): Promise<void> {
    return this.active.initialize();
  }

  voices(): Voice[] {
    return this.active.voices();
  }

  setHandlers(handlers: TTSHandlers): void {
    this.handlers = handlers;
    this.active.setHandlers(handlers);
  }

  enqueue(chunk: SpeakChunk, opts: SpeakOptions): void {
    this.active.enqueue(chunk, opts);
  }

  stop(): void {
    this.active.stop();
  }

  get ready(): boolean {
    return this.active.ready;
  }

  providers(): TTSProvider[] {
    return [...TTS_PROVIDERS];
  }

  activeProvider(): TTSProviderId {
    return this.activeId;
  }

  onStatus(cb: (status: TTSStatus) => void): void {
    this.statusCb = cb;
  }

  async setProvider(id: TTSProviderId): Promise<void> {
    if (id === this.activeId) return;
    // Silence whatever is currently playing before we swap engines out from under the player.
    this.active.stop();

    if (id === 'kokoro' && !this.kokoro.ready) {
      this.emit({ provider: id, phase: 'loading', progress: 0 });
      try {
        await this.kokoro.load((progress) => this.emit({ provider: id, phase: 'loading', progress }));
      } catch (error) {
        // Stay on the current provider; the player surfaces the error to the user.
        this.emit({ provider: id, phase: 'error', progress: 0 });
        throw error;
      }
    }

    this.active = id === 'kokoro' ? this.kokoro : this.system;
    this.activeId = id;
    this.active.setHandlers(this.handlers);
    this.emit({ provider: id, phase: 'ready', progress: 1 });
  }

  private emit(status: TTSStatus): void {
    this.statusCb?.(status);
  }
}
